import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Dialog, DossierSearch } from "../inbox/InboxDialogs";
import { PeopleSelects, SavFieldsGrid, useSavOptions } from "./SavFields";
import LitigeFields, { litigePayload, useLitigeOptions } from "../litiges/LitigeFields";

const MAIN = ["type_sav", "probleme_materiel", "description", "date_rdv"];
const KINDS = [["client", "Demande client"], ["sav", "Demande SAV"], ["litige", "Litige"], ["desinstallation", "Désinstallation"]];

export default function SavOpenDialog({ dossierId, onClose, onCreated, initialKind = "sav" }) {
  const opts = useSavOptions();
  const lopts = useLitigeOptions();
  const nav = useNavigate();
  const [did, setDid] = useState(dossierId || "");
  const [values, setValues] = useState({ type_sav: "Technique" });
  const [lit, setLit] = useState({});
  const [cvals, setCvals] = useState({});
  const [sub, setSub] = useState(null);
  const [emp, setEmp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState(initialKind);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = kind === "litige" ? await api.post("/litiges", { dossier_id: did, ...litigePayload(lit) })
        : await api.post("/sav", kind === "desinstallation" ? { dossier_id: did, kind } : kind === "client" ? { dossier_id: did, kind, values: cvals, employee_id: emp } : { dossier_id: did, kind, values, subcontractor_id: sub, employee_id: emp });
      toast.success(data.existing ? `Ce client a déjà la demande ${data.reference}` : `Demande ${data.reference} ouverte`);
      onCreated?.(data);
      nav(`/sav/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Création impossible"); }
    finally { setBusy(false); }
  };
  const fields = (opts?.fields || []).filter((f) => MAIN.includes(f.id));
  const what = { desinstallation: "la désinstallation", litige: "le litige", client: "la demande client" }[kind] || "le SAV";
  return (
    <Dialog title="Ouvrir une demande" onClose={onClose} testid="sav-open-dialog"
      footer={<><button onClick={onClose} className="btn-ghost text-sm">Annuler</button>
        <button onClick={create} disabled={busy || !did || !opts} className="btn-primary text-sm" data-testid="sav-open-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ouvrir {what}</button></>}>
      {!dossierId && <div className="space-y-1"><div className="text-[11px] text-norea-textMuted">Client (dossier)</div><DossierSearch value={did} onChange={setDid} /></div>}
      <div className="inline-flex p-0.5 rounded-md border border-norea-border bg-white" data-testid="sav-open-kind">
        {KINDS.filter(([k]) => !dossierId || k !== "desinstallation").map(([k, l]) => (
          <button key={k} type="button" onClick={() => setKind(k)} className={`px-3 py-1 text-xs rounded transition-colors ${kind === k ? "bg-norea-primary text-white" : "text-norea-textMuted hover:text-norea-text"}`} data-testid={`sav-open-kind-${k}`}>{l}</button>
        ))}
      </div>
      {kind === "litige" ? <LitigeFields opts={lopts} v={lit} onChange={setLit} />
        : kind === "desinstallation" ? <div className="text-[11px] text-norea-textMuted" data-testid="sav-open-desinst-hint">Une seule demande de désinstallation par client : protocole, date de désinstallation et annulation du financement se suivent dans la demande.</div>
        : !opts ? <Loader2 className="w-4 h-4 animate-spin" /> : (
        <>
          <SavFieldsGrid fields={kind === "client" ? opts.client_fields : fields} values={kind === "client" ? cvals : values}
            onChange={(k, v) => (kind === "client" ? setCvals : setValues)((x) => ({ ...x, [k]: v }))} />
          <PeopleSelects opts={opts} sub={sub} emp={emp} onSub={setSub} onEmp={setEmp} noSub={kind === "client"} />
          <div className="text-[11px] text-norea-textMuted">Les coordonnées du client sont reprises du dossier (modifiables ensuite). Les autres champs se complètent dans la demande.</div>
        </>
      )}
    </Dialog>
  );
}
